const DATE_ONLY_VALUE_REGEX = /^(\d{4})-(\d{2})-(\d{2})$/;

export const toDateKey = (value) => {
  const parsed = parseDateValue(value);
  if (!parsed) return null;
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const parseDateValue = (value) => {
  if (value == null || value === "") return null;

  if (value instanceof Date) {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;

    const match = trimmed.match(DATE_ONLY_VALUE_REGEX);
    if (match) {
      const year = Number(match[1]);
      const monthIndex = Number(match[2]) - 1;
      const day = Number(match[3]);
      const parsed = new Date(year, monthIndex, day);

      if (
        parsed.getFullYear() !== year ||
        parsed.getMonth() !== monthIndex ||
        parsed.getDate() !== day
      ) {
        return null;
      }

      return parsed;
    }
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const normalizeDateValue = (value, endOfDay = false) => {
  const parsed = parseDateValue(value);
  if (!parsed) return null;
  parsed.setHours(endOfDay ? 23 : 0, endOfDay ? 59 : 0, endOfDay ? 59 : 0, endOfDay ? 999 : 0);
  return parsed;
};

export const getMondayWeekdayIndex = (date) => (date.getDay() + 6) % 7;

export const buildBlockedDateSet = (values) =>
  new Set(
    (Array.isArray(values) ? values : [])
      .map((value) => toDateKey(value))
      .filter(Boolean)
  );

const cloneDateWithTime = (date, timeSource) => {
  const next = new Date(date);
  next.setHours(
    timeSource.getHours(),
    timeSource.getMinutes(),
    timeSource.getSeconds(),
    timeSource.getMilliseconds()
  );
  return next;
};

const clampDateToBounds = (candidate, minBound, maxBound) => {
  if (minBound && candidate < minBound) {
    return cloneDateWithTime(minBound, candidate);
  }

  if (maxBound && candidate > maxBound) {
    return cloneDateWithTime(maxBound, candidate);
  }

  return candidate;
};

export const resolveSelectableDate = ({
  value,
  minDate = null,
  maxDate = null,
  disabledWeekdays = [],
  blockedDates = [],
  today = new Date(),
  fallbackValue = new Date(),
} = {}) => {
  const minBound = normalizeDateValue(minDate, false);
  const maxBound = normalizeDateValue(maxDate, true);
  const fallbackDate = parseDateValue(fallbackValue) ?? new Date();
  const initialCandidate = parseDateValue(value) ?? fallbackDate;
  const clampedCandidate = clampDateToBounds(initialCandidate, minBound, maxBound);
  const currentDay = normalizeDateValue(today, false);
  const latestDay = maxBound ? normalizeDateValue(maxBound, false) : null;
  const disabledWeekdaysSet =
    disabledWeekdays instanceof Set
      ? disabledWeekdays
      : new Set(
        (Array.isArray(disabledWeekdays) ? disabledWeekdays : []).map((day) => Number(day))
      );
  const blockedDateSet =
    blockedDates instanceof Set ? blockedDates : buildBlockedDateSet(blockedDates);

  let cursor = normalizeDateValue(clampedCandidate, false) ?? normalizeDateValue(fallbackDate, false);

  if (currentDay && cursor < currentDay) {
    cursor = new Date(currentDay);
  }

  if (minBound && cursor < minBound) {
    cursor = new Date(minBound);
  }

  for (let i = 0; i < 732; i += 1) {
    if (latestDay && cursor > latestDay) break;

    const cursorKey = toDateKey(cursor);
    if (
      !disabledWeekdaysSet.has(getMondayWeekdayIndex(cursor)) &&
      !(cursorKey && blockedDateSet.has(cursorKey))
    ) {
      const next = new Date(cursor);
      next.setHours(
        clampedCandidate.getHours(),
        clampedCandidate.getMinutes(),
        clampedCandidate.getSeconds(),
        clampedCandidate.getMilliseconds()
      );
      return clampDateToBounds(next, minBound, maxBound);
    }

    const candidate = new Date(cursor);
    candidate.setDate(cursor.getDate() + 1);
    cursor = candidate;
  }

  return clampedCandidate;
};

const dateUtils = {
  getMondayWeekdayIndex,
  buildBlockedDateSet,
  normalizeDateValue,
  parseDateValue,
  resolveSelectableDate,
  toDateKey,
};

export default dateUtils;
